import z from 'zod';
import {
  BaseTicketEvent,
  EditableTicketFields,
  DeleteAttachmentTicketEvent,
  DeleteMessageTicketEvent,
  DeleteLocationTicketEvent,
  NotificationOptInTicketEvent,
  NotificationOptOutTicketEvent,
  UpdateLocationTicketEvent,
  UpdateMessageTicketEvent,
  TicketEvent,
  AddLocationTicketEvent,
  AddMessageTicketEvent,
  AddAttachmentTicketEvent,
  CreateTicketEvent,
  UpdateTicketEvent,
} from './Ticket.types';

const zBaseTicketEvent = {
  id: z.uuid(),
  recnum: z.int().optional(),
  ticket_id: z.uuid(),
  created: z.date(),
  inserted: z.date().nullable().optional(),
  user_id: z.int(),
  meta: z.record(z.string(), z.unknown()).optional(), // FIXME: omitting meta does not cause 'satisfies' to fail
} as const satisfies {
  [P in keyof Omit<BaseTicketEvent, 'payload' | 'type'>]: z.ZodType<
    any,
    any,
    any
  >;
};

export const zEditibleTicketFields = {
  title: z.string().optional(),
  description: z.string().optional(),
  status: z.enum(['opened', 'closed']).optional(),
  due: z.date().optional().nullable(),
  // CRM chain (klant → project → werk), all-or-nothing; see
  // docs/architecture/CRM_REFERENCE_PATTERN.md. Only werk_id is nullable —
  // `werk_id: null` is the project-level shape. A null project_id/client_id
  // means a writer could not resolve the chain, so it must not validate.
  // project_id non-null assumes werken have a project: true in practice, not
  // enforced by the column.
  project_id: z.int().optional(),
  werk_id: z.int().nullable().optional(),
  client_id: z.int().optional(),
  assignee_user_id: z.int().nullable().optional(),
  executor_user_id: z.int().nullable().optional(),
} as const satisfies {
  [P in keyof EditableTicketFields]: z.ZodType<any, any, any>;
};

const CRM_CHAIN_KEYS = ['client_id', 'project_id', 'werk_id'] as const;

/** A payload either leaves the CRM chain alone, or carries all of it. */
function checkCrmChain(
  ctx: z.core.ParsePayload<{
    project_id?: number;
    werk_id?: number | null;
    client_id?: number;
  }>,
  { requireReference }: { requireReference: boolean },
): void {
  const missing = CRM_CHAIN_KEYS.filter((key) => ctx.value[key] === undefined);

  if (missing.length === CRM_CHAIN_KEYS.length) {
    // Chain untouched.
    if (requireReference) {
      ctx.issues.push({
        code: 'custom',
        message: 'A ticket must reference a project or werk',
        input: ctx.value,
      });
    }
    return;
  }

  if (missing.length > 0) {
    ctx.issues.push({
      code: 'custom',
      path: [missing[0]!],
      message: `CRM chain must be explicit — missing: ${missing.join(', ')}`,
      input: ctx.value,
    });
  }
}

export const zCreateTicketEvent = z.strictObject({
  type: z.literal('CREATE_TICKET'),
  payload: z
    .strictObject({
      ...zEditibleTicketFields,
      title: z.string(),
      add_labels: z.array(z.int()).optional(),
    })
    .check((ctx) => checkCrmChain(ctx, { requireReference: true })),
  ...zBaseTicketEvent,
}) satisfies z.ZodType<CreateTicketEvent>;

export const zUpdateTicketEvent = z.strictObject({
  type: z.literal('UPDATE_TICKET'),
  payload: z
    .strictObject({
      ...zEditibleTicketFields,
      add_labels: z.array(z.int()).optional(),
      remove_labels: z.array(z.int()).optional(),
    })
    .check((ctx) => checkCrmChain(ctx, { requireReference: false })),
  ...zBaseTicketEvent,
}) satisfies z.ZodType<UpdateTicketEvent>;

export const zAddAttachmentEvent = z.strictObject({
  type: z.literal('ADD_ATTACHMENT'),
  payload: z.strictObject({
    checksum: z.string(),
    size: z.number(),
    contentType: z.string(),
    filename: z.string().optional(),
  }),
  ...zBaseTicketEvent,
}) satisfies z.ZodType<AddAttachmentTicketEvent>;

export const zAddLocationEvent = z.strictObject({
  type: z.literal('ADD_LOCATION'),
  payload: z.strictObject({
    label: z.string(),
    position: z.strictObject({
      coords: z.strictObject({
        speed: z.number().nullable(),
        heading: z.number().nullable(),
        accuracy: z.number().nullable(),
        altitude: z.number().nullable(),
        altitudeAccuracy: z.number().nullable(),
        latitude: z.number(),
        longitude: z.number(),
      }),
      timestamp: z.number(),
    }),
  }),
  ...zBaseTicketEvent,
}) satisfies z.ZodType<AddLocationTicketEvent>;
export const zAddMessageEvent = z.strictObject({
  type: z.literal('ADD_MESSAGE'),
  payload: z.strictObject({
    message: z.string(),
  }),
  ...zBaseTicketEvent,
}) satisfies z.ZodType<AddMessageTicketEvent>;

export const zDeleteAttachmentEvent = z.strictObject({
  type: z.literal('DELETE_ATTACHMENT'),
  payload: z.strictObject({
    eventId: z.uuid(),
  }),
  ...zBaseTicketEvent,
}) satisfies z.ZodType<DeleteAttachmentTicketEvent>;

export const zDeleteMessageEvent = z.strictObject({
  type: z.literal('DELETE_MESSAGE'),
  payload: z.strictObject({
    eventId: z.uuid(),
  }),
  ...zBaseTicketEvent,
}) satisfies z.ZodType<DeleteMessageTicketEvent>;

export const zDeleteLocationEvent = z.strictObject({
  type: z.literal('DELETE_LOCATION'),
  payload: z.strictObject({
    eventId: z.uuid(),
  }),
  ...zBaseTicketEvent,
}) satisfies z.ZodType<DeleteLocationTicketEvent>;

export const zNotificationOptInEvent = z.strictObject({
  type: z.literal('NOTIFICATION_OPT_IN'),
  payload: z.strictObject({}),
  ...zBaseTicketEvent,
}) satisfies z.ZodType<NotificationOptInTicketEvent>;

export const zNotificationOptOutEvent = z.strictObject({
  type: z.literal('NOTIFICATION_OPT_OUT'),
  payload: z.strictObject({}),
  ...zBaseTicketEvent,
}) satisfies z.ZodType<NotificationOptOutTicketEvent>;

export const zUpdateLocationEvent = z.strictObject({
  type: z.literal('UPDATE_LOCATION'),
  payload: z.strictObject({
    eventId: z.uuid(),
    label: z.string(),
  }),
  ...zBaseTicketEvent,
}) satisfies z.ZodType<UpdateLocationTicketEvent>;

export const zUpdateMessageEvent = z.strictObject({
  type: z.literal('UPDATE_MESSAGE'),
  payload: z.strictObject({
    eventId: z.uuid(),
    message: z.string(),
  }),
  ...zBaseTicketEvent,
}) satisfies z.ZodType<UpdateMessageTicketEvent>;

export const zTicketEvent: z.ZodType<TicketEvent> = z.discriminatedUnion(
  'type',
  [
    zCreateTicketEvent,
    zUpdateTicketEvent,
    zAddAttachmentEvent,
    zAddLocationEvent,
    zAddMessageEvent,
    zDeleteAttachmentEvent,
    zDeleteMessageEvent,
    zDeleteLocationEvent,
    zNotificationOptInEvent,
    zNotificationOptOutEvent,
    zUpdateLocationEvent,
    zUpdateMessageEvent,
  ] as const,
);
